import { useDispatch } from "react-redux";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { HEADER_DATA_UPDATE } from "../../../redux/reducers/header.reducer";
import { EstatusDocumentoEnum } from "../../../@enums/status_documento.enum";
import { Alert, Autocomplete, Box, Button, Checkbox, Chip, FormControl, Grid, TextField, Tooltip, Typography } from "@mui/material";
import { Search, RestartAlt, Undo, AddCircle } from "@mui/icons-material";
import { DeleteTagMultiple, GetOnlyAjuste } from "../../../hooks/items-ajuste";
import { useInfiniteItemsAjusteCliente, ProcessAllCliente, ProcessPartialCliente } from "../../../hooks/ajusteCliente";
import { GetAllPartesList } from "../../../hooks/getPartesList";
import { GetAllAlmacenesList } from "../../../hooks/getListAlmacen";
import { useLoading } from "../../../components/Loading/useLoading";
import { TiposAlmacenEnum } from "../../../@enums/tipo_almacen";
import { useSnackbar } from "notistack";
import swal from 'sweetalert2'
import { AddTag, CloseSalidaInventario, ReactivarEtiqueta, RemoveTagOrderOpen, UndoSalidaInventario } from "../../../hooks/ajuste";
import { TipoDocumentoEnum } from "../../../@enums/tipo_documento";
import moment from "moment";
import { EstatusMovimientoEnum } from "../../../@enums/status_mov_inv";

export const AjusteCliente = () => {
  const { id, empresa } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { setAppLoading } = useLoading();
  const { enqueueSnackbar } = useSnackbar();
  const { addTagMutation } = AddTag();
  const { getOnlyAjusteMutation } = GetOnlyAjuste();
  const { getAllPartesListMutation } = GetAllPartesList();
  const { getAllAlmacenesListMutation } = GetAllAlmacenesList();
  const { processAllClienteMutation } = ProcessAllCliente();
  const { processPartialClienteMutation } = ProcessPartialCliente();
  const { deleteTagMultipleMutation } = DeleteTagMultiple();
  const { removeTagOrderOpenMutation } = RemoveTagOrderOpen();
  const { reactivarEtiquetaMutation } = ReactivarEtiqueta();
  const { closeSalidaInventarioMutation } = CloseSalidaInventario();
  const { undoSalidaInventarioMutation } = UndoSalidaInventario();

  const [ajuste, setAjuste] = useState(null);
  const [isClosed, setIsClosed] = useState(false);
  const [filtro, setFiltro] = useState("");
  const [partes, setPartes] = useState([]);
  const [parte, setParte] = useState(null);
  const [almacenes, setAlmacenes] = useState([]);
  const [almacen, setAlmacen] = useState(null);
  const [filtros, setFiltros] = useState(null);
  const [itemInc, setItemInc] = useState([]);
  const [confirmClose, setConfirmClose] = useState(false);
  const [confirmProcess, setConfirmProcess] = useState(false);

  // reglas de selección
  const [selectionRules, setSelectionRules] = useState([]);

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isRefetching,
    refetch,
  } = useInfiniteItemsAjusteCliente(id, empresa, null, filtros, isClosed, []);

  const items = useMemo(() => {
    if (!data?.pages?.length) return [];
    return data.pages.flatMap((p) => p.items || []);
  }, [data]);

  const sentinelRef = useRef(null);

  useEffect(() => {
    if (!sentinelRef.current) return;
    const el = sentinelRef.current;
    const observer = new IntersectionObserver(
      (entries) => {
        const first = entries[0];
        if (first.isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { root: null, rootMargin: "20px", threshold: 0 }
    );
    observer.observe(el);

    return () => observer.unobserve(el);
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  useEffect(() => {
    if (isRefetching) setAppLoading(true);
    else setAppLoading(false);
  }, [isRefetching]);

  useEffect(() => {
    get_ajuste();
    get_clientes();
    get_almacenes();
  }, []);

  const get_ajuste = () => {
    setAppLoading(false);
    getOnlyAjusteMutation(id, {
      onSuccess: (data) => {
        if (data) {
          dispatch(HEADER_DATA_UPDATE({ nombreData: `Ajuste Folio ${data?.doc?.docu_folio}` }));
          data?.doc?.docu_estatus === EstatusDocumentoEnum.CERRADO ? setIsClosed(true) : setIsClosed(false);
          setAjuste(data);
        }
      },
      onError: (error) => console.log(error),
      onSettled: () => setAppLoading(false),
    });
  };

  const get_clientes = () => {
    setAppLoading(true);
    getAllPartesListMutation(
      {},
      {
        onSuccess: (data) => setPartes(data.filter((part) => part.emp_id === +empresa)),
        onError: (error) => console.log(error),
        onSettled: () => setAppLoading(false),
      }
    );
  };

  const get_almacenes = () => {
    setAppLoading(true);
    getAllAlmacenesListMutation(
      {},
      {
        onSuccess: (data) => setAlmacenes(data.filter((item) => item.tipo === TiposAlmacenEnum.ALMACEN)),
        onError: (error) => console.log(error),
        onSettled: () => setAppLoading(false),
      }
    );
  };

  // === Alias solicitados ===
  // aplicar_filtro: arma el objeto filtros y hace refetch
  const aplicar_filtro = () => {
    const data_f = {};
    if (parte) data_f.parte = parte?.value;
    if (almacen) data_f.almacen = { almacen: almacen?.label, alma_id: almacen?.id };
    if (filtro && filtro !== '') data_f.tag = filtro;

    if (Object.keys(data_f).length > 0) {
      setFiltros(data_f);
    } else {
      setFiltros(null);
    }
    refetch();
  };

  // reset_filtro: limpia filtros y hace refetch
  const reset_filtro = () => {
    setAlmacen(null);
    setParte(null);
    setFiltro('');
    setFiltros(null);
    refetch();
  };

  const handleInputChange = (event) => setFiltro(event.target.value);

  // helpers selección
  const itemKey = (mov) => mov?.domo_id ?? mov?.tag;
  const matchesRule = (mov, rule) => {
    if (rule.id && itemKey(mov) !== rule.id) return false;
    if (mov?.fromDocMov) return false
    if (rule.partId && mov?.part_id !== rule.partId) return false;
    if (rule.almacenId && mov?.alma_id !== rule.almacenId) return false;
    return true;
  };
  const isItemSelected = (mov) => {
    let selected = false;
    for (const rule of selectionRules) {
      if (rule.id) {
        if (itemKey(mov) === rule.id) selected = rule.op === "select";
      } else if (matchesRule(mov, rule)) {
        selected = rule.op === "select";
      }
    }
    return selected;
  };

  // alcance actual de filtros (parte + almacén + etiqueta)
  const currentScope = useMemo(() => ({
    partId: parte?.value ?? null,
    almacenId: almacen?.id ?? null,
    filtroTag: filtro || null,
  }), [parte, almacen, filtro]);

  // items que cumplen el alcance
  const scopeItems = useMemo(() => {
    return items.filter((mov) => {
      if (currentScope.partId && mov?.part_id !== currentScope.partId) return false;
      if (currentScope.almacenId && mov?.alma_id !== currentScope.almacenId) return false;
      if (currentScope.filtroTag && !mov?.tag?.includes(currentScope.filtroTag)) return false;
      return true;
    });
  }, [items, currentScope]);

  // ¿todo el alcance está seleccionado?
  const isAllInScopeSelected = useMemo(() => {
    const base = items.filter((mov) => {
      if (mov?.fromDocMov) return false;
      if (currentScope.partId && mov?.part_id !== currentScope.partId) return false;
      if (currentScope.almacenLabel && mov?.almacen !== currentScope.almacenLabel) return false;
      return true;
    });
    if (base.length === 0) return false;
    return base.every((mov) => isItemSelected(mov));
  }, [items, currentScope, selectionRules]);

  const applyScopeRule = (op) => {
    console.log(op)
    console.log(currentScope)
    if (op === "deselect" && !currentScope.partId && !currentScope.almacenId && !currentScope.filtroTag) {
      setSelectionRules([]);
    } else {
      setSelectionRules((prev) => [
        ...prev,
        {
          op,
          partId: currentScope.partId,
          almacenId: currentScope.almacenId,
          filtroTag: currentScope.filtroTag,
        },
      ]);
    }
    setSelectionRules((prev) => [
      ...prev,
      {
        op,
        partId: currentScope.partId,
        almacenId: currentScope.almacenId,
        filtroTag: currentScope.filtroTag,
      },
    ]);
  };

  const handleToggleScopeSelectAll = () => {
    if (isAllInScopeSelected) applyScopeRule("deselect");
    else applyScopeRule("select");
  };

  const toggleSingleItem = (mov) => {
    if (mov?.fromDocMov) {
      let exist = itemInc.findIndex(i => i === mov?.domo_id)
      if (exist === -1) {
        setItemInc(prev => [...prev, mov?.domo_id])
      } else {
        setItemInc(
          prev => [...prev.filter(p => p !== mov?.domo_id)]
        )
      }
      setSelectionRules([])
    } else {
      const selected = isItemSelected(mov);
      setSelectionRules((prev) => [...prev, { op: selected ? "deselect" : "select", id: itemKey(mov) }]);
    }
  };

  const processParcial = (rules, tagsOmit, tagsAdmit) => {
    setAppLoading(true)
    processPartialClienteMutation(
      {
        emp_id: empresa,
        rules,
        tagsOmit,
        tagsAdmit
      },
      {
        onSuccess: (data) => {
          enqueueSnackbar("Datos procesados con exito", {
            autoHideDuration: 2000,
            variant: data?.success ? "success" : "error",
            anchorOrigin: {
              vertical: "top",
              horizontal: "center",
            },
          });
        },
        onError: (error) => {
          console.log(error)
        },
        onSettled: () => {
          cleanAll();
          refetch();
          setAppLoading(false)
        }
      }
    )
  }
  const processAll = (rules, tagsOmit, tagsAdmit) => {
    processAllClienteMutation(
      {
        emp_id: empresa,
        rules,
        tagsOmit,
        tagsAdmit
      },
      {
        onSuccess: (data) => {
          enqueueSnackbar("Datos procesados con exito", {
            autoHideDuration: 2000,
            variant: data?.success ? "success" : "error",
            anchorOrigin: {
              vertical: "top",
              horizontal: "center",
            },
          });
        },
        onError: (error) => {
          console.log(error)
        },
        onSettled: () => {
          cleanAll();
          refetch();
          setAppLoading(false)
        }
      }
    )
  }
  const deleteMultiple = () => {
    swal.fire({
      title: "¿Esta seguro que desea procesar todas estas etiquetas?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Sí, agregar"
    }).then((result) => {
      if (result.isConfirmed) {
        setAppLoading(true);
        deleteTagMultipleMutation({ domo_id: itemInc, isCliente: true }, {
          onSuccess: (data) => {
            enqueueSnackbar(data?.mensaje, {
              autoHideDuration: 2000,
              variant: data?.success ? "success" : "error",
              anchorOrigin: {
                vertical: "top",
                horizontal: "center",
              },
            });
          },
          onError: (error) => {
            enqueueSnackbar("Hubo un error al procesar, inténtelo nuevamente", {
              autoHideDuration: 2000,
              variant: "error",
              anchorOrigin: {
                vertical: "top",
                horizontal: "center",
              },
            });
          },
          onSettled: () => {
            cleanAll();
            refetch();
            setAppLoading(false);
          }
        })
      }
    })
  }
  const cleanAll = () => {
    setSelectionRules([]);
    setItemInc([]);
    setConfirmProcess(false);
    setConfirmClose(false);
    reset_filtro();
  }

  const buildSelectionRules = () => {
    console.log(selectionRules);
    let reglas = [];
    let tagsInc = [];
    let tagsExc = [];
    let forAll = false;

    if (itemInc.length > 0) {
      deleteMultiple();
    } else {
      for (const rule of selectionRules) {
        if (rule.op === "select") {
          if (!rule.id && !rule.partId && !rule.almacenId) {
            reglas = [];
            tagsInc = [];
            tagsExc = [];
            forAll = true
          } // skip empty rules
          if (rule.id) {
            tagsInc.push(rule.id);
            let exist = tagsExc.findIndex(t => t === rule.id);
            if (exist !== -1) {
              tagsExc.splice(exist, 1);
            }
          } else if (rule.partId) {
            let existingRule = reglas.findIndex(r => r.parte === rule.partId);
            if (existingRule === -1) {
              let newRule = {
                parte: rule.partId,
                almacenes: [],
                almacenesExc: [],
              }
              if (rule.almacenId) {
                newRule.almacenes.push(rule.almacenId);
              }
              reglas.push(newRule);
            } else if (existingRule !== -1 && rule.almacenId) {
              reglas[existingRule].almacenesExc = reglas[existingRule].almacenesExc.filter(a => a !== rule.almacenId);
              if (reglas[existingRule].almacenes.length > 0) {
                reglas[existingRule].almacenes.push(rule.almacenId);
              }
            }

          } else if (rule.almacenId) {
            let existingRule = reglas.findIndex(r => r.almacen === rule.almacenId);
            if (existingRule === -1) {
              let newRule = {
                almacen: rule.almacenId,
                partes: [],
                partesExc: [],
              }
              if (rule.partId) {
                newRule.partes.push(rule.partId);
              }
              reglas.push(newRule);
            } else if (existingRule !== -1 && rule.partId) {
              reglas[existingRule].partes.push(rule.partId);
            }
          }
        } else {
          if (!rule.id && !rule.partId && !rule.almacenId) {
            reglas = [];
            tagsInc = [];
            tagsExc = [];
            forAll = false;
          } else if (rule.id) {
            tagsExc.push(rule.id);
            let exist = tagsInc.findIndex(t => t === rule.id);
            if (exist !== -1) {
              tagsInc.splice(exist, 1);
            }
          } else if (rule.partId) {
            let existingRule = reglas.findIndex(r => r.parte === rule.partId);
            if (existingRule === -1) {
              let existingAlm = reglas.findIndex(r => r.almacen === rule.almacenId);
              if (existingAlm !== -1) {
                reglas[existingAlm].partes = reglas[existingAlm]?.partes?.filter(p => p !== rule.partId);
                reglas[existingAlm].partesExc.push(rule.partId);
              }
            }
            else if (rule.almacenId) {
              reglas[existingRule].almacenesExc.push(rule.almacenId);
              reglas[existingRule].almacenes = reglas[existingRule]?.almacenes?.filter(a => a !== rule.almacenId);
            } else {
              reglas = reglas.filter(r => r.parte !== rule.partId);
            }
          } else if (rule.almacenId) {
            let existingRule = reglas.findIndex(r => r.almacen === rule.almacenId);
            if (!rule.partId && existingRule !== -1) {
              reglas = reglas.filter(r => r.almacen !== rule.almacenId);
            }
          }
        }
      }

      if (forAll) {
        processAll(reglas, tagsExc, tagsInc);
      } else {
        processParcial(reglas, tagsExc, tagsInc)
      }
    }


  }

  //ELIMINAR ETIQUETA ORDEN ABIERTA
  const remove_tag_open = (id) => {
    setAppLoading(true);
    deleteTagMultipleMutation({ domo_id: [id], isCliente: true }, {
      onSuccess: (data) => {
        enqueueSnackbar(data?.mensaje, {
          autoHideDuration: 2000,
          variant: data?.success ? "success" : "error",
          anchorOrigin: {
            vertical: "top",
            horizontal: "center",
          },
        });
      },
      onError: (error) => {
        enqueueSnackbar("Hubo un error al procesar, inténtelo nuevamente", {
          autoHideDuration: 2000,
          variant: "error",
          anchorOrigin: {
            vertical: "top",
            horizontal: "center",
          },
        });
      },
      onSettled: () => {
        cleanAll();
        refetch();
        setAppLoading(false);
      }
    })
    // removeTagOrderOpenMutation({ id, tipo: ajuste?.doc?.docu_tipo }, {
    //   onSuccess: (data) => {
    //     enqueueSnackbar(data?.mensaje, {
    //       autoHideDuration: 2000,
    //       variant: data?.success ? "success" : "error",
    //       anchorOrigin: {
    //         vertical: "top",
    //         horizontal: "center",
    //       },
    //     });
    //   },
    //   onError: (error) => {
    //     enqueueSnackbar("Hubo un error al procesar", {
    //       autoHideDuration: 2000,
    //       variant: "error",
    //       anchorOrigin: {
    //         vertical: "top",
    //         horizontal: "center",
    //       },
    //     });
    //   },
    //   onSettled: () => {
    //     refetch();
    //     setAppLoading(false);
    //   }
    // })
  }
  const add_tag = (tag) => {
    swal.fire({
      title: "¿Esta seguro que desea agregar esta etiqueta a la salida?",
      text: "Desaparecerá del almacén y se eliminará al cerrar la salida",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Sí, agregar"
    }).then((result) => {
      if (result.isConfirmed) {
        setAppLoading(true);
        addTagMutation({ tag, docu_tipo: TipoDocumentoEnum.AJUSTE_CLIENTE, isCliente: true, emp_id: empresa }, {
          onSuccess: (data) => {
            enqueueSnackbar(data?.mensaje, {
              autoHideDuration: 2000,
              variant: data?.success ? "success" : "error",
              anchorOrigin: {
                vertical: "top",
                horizontal: "center",
              },
            });
          },
          onError: (error) => {
            enqueueSnackbar("Hubo un error al procesar, inténtelo nuevamente", {
              autoHideDuration: 2000,
              variant: "error",
              anchorOrigin: {
                vertical: "top",
                horizontal: "center",
              },
            });
          },
          onSettled: () => {
            refetch();
            setAppLoading(false);
          }
        })
      }
    })
  }
  //REACTIVAR ETIQUETA
  const delete_tag = (movdoc_id) => {
    swal.fire({
      title: "¿Esta seguro que desea reestablecer esta etiqueta del almacén?",
      text: "Aparecera de nuevo en el almacén y tendra que agregarla a una orden abierta para poder retirarla del almacén nuevamente",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Sí, reestablecer"
    }).then((result) => {
      if (result.isConfirmed) {
        setAppLoading(true);
        reactivarEtiquetaMutation(movdoc_id, {
          onSuccess: (data) => {
            if (data?.success) {
              enqueueSnackbar('La etiqueta ha sido reactivada', {
                autoHideDuration: 2000,
                variant: "success",
                anchorOrigin: {
                  vertical: "top",
                  horizontal: "center",
                },
              });
            } else {
              enqueueSnackbar('Ha habido un problema al reactivar', {
                autoHideDuration: 2000,
                variant: "error",
                anchorOrigin: {
                  vertical: "top",
                  horizontal: "center",
                },
              });
            }
          },
          onError: (error) => {
            console.log(error);
            enqueueSnackbar('Ha habido un problema al reactivar', {
              autoHideDuration: 2000,
              variant: "error",
              anchorOrigin: {
                vertical: "top",
                horizontal: "center",
              },
            });
          },
          onSettled: () => {
            refetch();
            setAppLoading(false)
          }
        })
      }
    })

  }

  const confirmCloseAjuste = () => {
    reset_filtro();
    setConfirmClose(true);
  }

  //CERRAR AJUSTE
  const cerrar_ajuste = () => {
    swal.fire({
      title: "¿Esta seguro que desea realizar el ajuste del almacén?",
      text: "Se eliminarán todos los movimientos que han sido agregados en el almacén y sus respectivas masters",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Sí, realizar ajuste"
    }).then((result) => {
      if (result.isConfirmed) {
        setAppLoading(true);
        closeSalidaInventarioMutation({ docu_id: ajuste?.doc?.docu_id, isClient: true }, {
          onSuccess: (data) => {
            console.log(data);
            setAppLoading(false);
            enqueueSnackbar(data?.mensaje, {
              autoHideDuration: 2000,
              variant: data?.success ? "success" : "error",
              anchorOrigin: {
                vertical: "top",
                horizontal: "center",
              },
            });
            navigate(`/inventarios/ajuste-inventario-cliente`);
          },
          onError: (error) => {
            console.log(error);
            setAppLoading(false);
          }
        })
      }
    })
  }

  //ELIMINAR SALIDA COMPLETA
  const undo_salida = () => {
    swal.fire({
      title: "¿Esta seguro que desea reestablecer todas las etiquetas de esta salida?",
      text: "Aparecerán de nuevo en el almacén y tendrá que agregarlas a una orden abierta para poder retirarlas del almacén nuevamente",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Sí, reestablecer"
    }).then((result) => {
      if (result.isConfirmed) {
        setAppLoading(true);
        undoSalidaInventarioMutation(ajuste?.doc?.docu_id, {
          onSuccess: (data) => {
            console.log(data);
            if (data?.success) {
              enqueueSnackbar('Las etiquetas han sido reactivadas', {
                autoHideDuration: 2000,
                variant: "success",
                anchorOrigin: {
                  vertical: "top",
                  horizontal: "center",
                },
              });
            } else {
              enqueueSnackbar('Ha habido un problema al reactivar', {
                autoHideDuration: 2000,
                variant: "error",
                anchorOrigin: {
                  vertical: "top",
                  horizontal: "center",
                },
              });
            }
            setAppLoading(false);
            navigate(`/inventarios/ajuste-inventario-cliente`);
          },
          onError: (error) => {
            console.log(error);
            setAppLoading(false);
            enqueueSnackbar('Ha habido un problema al reactivar', {
              autoHideDuration: 2000,
              variant: "error",
              anchorOrigin: {
                vertical: "top",
                horizontal: "center",
              },
            });
          }
        })
      }
    })
  }

  return (
    <>
      <Grid container spacing={0}>
        <Grid item xs={12} sx={{ p: 1, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography variant="h5"></Typography>
          {isClosed ?
            (
              <Button variant="contained" className="btn_system" sx={{ width: 200, ml: 1 }} onClick={undo_salida}>
                Reestablecer Ajuste
              </Button>
            ) :
            (
              <>
                {
                  confirmClose ?
                    <Button variant="contained" className="btn_system" sx={{ width: 200, ml: 1 }} onClick={cerrar_ajuste}>
                      Confirmar cierre
                    </Button> :
                    <Button variant="contained" className="btn_system" sx={{ width: 200, ml: 1 }} onClick={confirmCloseAjuste}>
                      Cerrar Ajuste
                    </Button>

                }
              </>
            )
          }
        </Grid>

        {/* Cards */}
        {/* Filtros */}
        <Grid item xs={12} sx={{ p: 1, textAlign: 'start', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Grid container spacing={0}>
            <Grid item xs={2} sx={{ display: 'flex', alignItems: 'center', p: 1 }}>
              <FormControl fullWidth size="small">
                <Autocomplete
                  fullWidth
                  id="part_id"
                  getOptionLabel={(opcion) =>
                    opcion?.codigo !== undefined ?
                      `${opcion?.label} / ${opcion?.codigo} / ${opcion?.codigo_salida} / ${opcion?.codigo_alterno}` :
                      opcion?.label
                  }
                  filterSelectedOptions
                  options={partes}
                  onChange={(e, newValue) => setParte(newValue ?? null)}
                  renderOption={(props, option) => (
                    <Box {...props} key={option?.value}>
                      {option?.codigo !== undefined ? option?.codigo : <></>} /
                      {option?.codigo_salida !== undefined ? option?.codigo_salida : <></>} /
                      {option?.codigo_alterno !== undefined ? option?.codigo_alterno : <></>} / {option?.label}
                    </Box>
                  )}
                  value={parte}
                  renderInput={(params) => <TextField {...params} label="Parte" size="small" fullWidth required />}
                />
              </FormControl>
            </Grid>
            <Grid item xs={2} sx={{ display: 'flex', alignItems: 'center', p: 1 }}>
              <FormControl fullWidth size="small">
                <Autocomplete
                  fullWidth
                  id="alm_id"
                  getOptionLabel={(opcion) => opcion.label}
                  filterSelectedOptions
                  options={almacenes}
                  value={almacen}
                  onChange={(e, newValue) => setAlmacen(newValue ?? null)}
                  renderOption={(props, option) => (
                    <Box {...props} key={option?.id}>{option.label}</Box>
                  )}
                  renderInput={(params) => <TextField {...params} label="Almacén" size="small" fullWidth required />}
                />
              </FormControl>
            </Grid>
            <Grid item xs={2} sx={{ display: 'flex', alignItems: 'center', p: 1 }}>
              <TextField id="search_data" label="Etiqueta" name="search_data" size="small" value={filtro} onChange={handleInputChange} />
            </Grid>
            <Grid item xs={1} sx={{ display: 'flex', alignItems: 'center', p: 1 }}>
              <Tooltip title="Aplicar filtro" placement="right">
                <Button variant="contained" className="btn_system" onClick={aplicar_filtro} sx={{ width: 40, minWidth: 30, ml: 1 }}>
                  <Search />
                </Button>
              </Tooltip>
              {filtros && (
                <Tooltip title="Restaurar filtro" placement="right">
                  <Button variant="contained" className="btn_system" onClick={reset_filtro} sx={{ width: 40, minWidth: 30, ml: 1, backgroundColor: '#E0D528' }}>
                    <RestartAlt />
                  </Button>
                </Tooltip>
              )}
            </Grid>
          </Grid>
        </Grid>
        {
          !isClosed &&
          <Grid item xs={12} sx={{ p: 1, textAlign: 'start', display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mb: 2, p: 1 }}>
            <Grid item xs={2} sx={{ display: 'flex', alignItems: 'center', p: 1 }}>
              {
                confirmProcess ?
                  <Tooltip title="Procesar" placement="right">
                    <Button variant="contained" className="btn_system" onClick={buildSelectionRules} disabled={selectionRules.length === 0 && itemInc.length === 0} sx={{ ml: 1 }} >
                      Confirmar proceso
                    </Button>
                  </Tooltip> :
                  <Tooltip title="Procesar" placement="right">
                    <Button variant="contained" className="btn_system" onClick={() => {
                      reset_filtro();
                      setConfirmProcess(true);
                    }} disabled={selectionRules.length === 0 && itemInc.length === 0} sx={{ ml: 1 }} >
                      Procesar datos
                    </Button>
                  </Tooltip>
              }
            </Grid>
            <Grid item xs={2} sx={{ display: 'flex', alignItems: 'center', p: 1 }}>
              <Tooltip title={`${isAllInScopeSelected ? "Desmarcar todo" : "Marcar todo"}`} placement="right">
                <Button
                  variant="contained"
                  className="btn_system"
                  onClick={handleToggleScopeSelectAll}
                  disabled={itemInc.length > 0}
                  sx={{ ml: 1 }}
                >
                  {isAllInScopeSelected ? "Desmarcar todo" : "Marcar todo"}
                </Button>
              </Tooltip>
            </Grid>
          </Grid>
        }
        {
          confirmClose &&
          <Alert severity="info" variant="outlined" sx={{ width: '100%', mb: 1, p: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            Para cerrar el ajuste, es necesario confirmar, rectifique que no haya etiquetas pendientes de procesar.
            Lo resaltado en <span style={{ color: 'rgb(150, 1, 1)', fontWeight: 600 }}>Rojo</span> se eliminará del almacen y
            lo resaltado en <span style={{ color: '#575E3B', fontWeight: 600 }}>Verde</span> se mantendrá.
          </Alert>
        }
        {
          confirmProcess &&
          <Alert severity="info" variant="outlined" sx={{ width: '100%', mb: 1, p: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            Todo lo marcado por los checkboxes se procesará, confirme si desea continuar.
          </Alert>
        }

        <Grid item xs={12}>
          <Grid container spacing={0}>
            {items.map((mov, index) => (
              <Grid item xs={6} sm={6} md={4} lg={3} xl={3} key={itemKey(mov)} sx={{ p: 1 }}>
                <Chip
                  label={
                    <>
                      {
                        !isClosed &&
                        <div
                          style={{
                            backgroundColor: "transparent",
                            display: "flex",
                            alignItems: 'center',
                            justifyContent: 'flex-end',
                            width: '100%',
                            maxWidth: '100%',
                            marginLeft: 20
                          }}
                        >
                          <Checkbox
                            checked={mov?.fromDocMov ? itemInc.some(i => i === mov?.domo_id) : isItemSelected(mov)}
                            onChange={() => toggleSingleItem(mov)}
                            disabled={itemInc.length > 0 && !mov?.fromDocMov}
                          />
                        </div>
                      }
                      <div
                        style={{
                          backgroundColor: "transparent",
                          border: "none",
                          display: "flex",
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: '100%',
                          maxWidth: '100%'
                        }}
                      >
                        <span
                          style={{
                            marginRight: 7,
                            fontSize: 14,
                            fontWeight: 400,
                            color: "black",
                          }}
                        >
                          {mov?.quantity}
                        </span>
                        <span
                          style={{
                            marginLeft: 7,
                            border: mov?.fromDocMov ? "1px solid rgb(150, 1, 1)" : "1px solid #575E3B",
                            borderRadius: 4,
                            fontSize: 11,
                            padding: 3,
                            color: mov?.fromDocMov ? "rgb(170, 0, 0)" : "#575E3B"
                          }}
                        >
                          {mov?.tag}
                        </span>
                      </div>
                      <div
                        style={{
                          backgroundColor: "transparent",
                          border: "none",
                          width: '100%',
                          maxWidth: '100%',
                          textAlign: 'center'
                        }}
                      >
                        <span
                          style={{
                            marginLeft: 7,
                            fontSize: 15,
                            padding: 3,
                            color: "black"
                          }}
                        >
                          {`${mov?.name}`}
                        </span>
                        <br />
                        <span
                          style={{
                            marginLeft: 7,
                            fontSize: 15,
                            padding: 3,
                            color: "black"
                          }}
                        >
                          {`${mov?.code}`}
                        </span>
                        <br />
                        <span
                          style={{
                            marginLeft: 7,
                            fontSize: 15,
                            padding: 3,
                            color: "black"
                          }}
                        >
                          {`${mov?.almacen}`}
                        </span>
                        <br />
                        <span
                          style={{
                            marginLeft: 7,
                            border: mov?.fromDocMov ? "1px solid rgb(150, 1, 1)" : "1px solid #575E3B",
                            borderRadius: 4,
                            fontSize: 11,
                            padding: 3,
                            color: mov?.fromDocMov ? "rgb(170, 0, 0)" : "#575E3B"
                          }}
                        >
                          {
                            mov?.status === EstatusMovimientoEnum.CARGADO || mov?.prevStatus === EstatusMovimientoEnum.CARGADO ?
                              "Cargado" :
                              "Pie de línea"
                          }
                        </span>
                        <br />
                        <span
                          style={{
                            marginLeft: 7,
                            fontSize: 15,
                            padding: 3,
                            color: "black"
                          }}
                        >
                          {`${moment(mov?.fecha).format('DD-MM-YYYY')}`}
                        </span>
                      </div>
                    </>
                  }
                  variant="outlined"
                  deleteIcon={
                    mov?.fromDocMov ?
                      <Undo sx={{ marginLeft: "1px !important" }} />
                      : <AddCircle sx={{ marginLeft: "1px !important" }} />
                  }
                  onDelete={
                    () => isClosed ? delete_tag(mov?.domo_id) :
                      mov?.fromDocMov ? remove_tag_open(mov?.domo_id) : add_tag(mov?.tag)
                  }
                  sx={{
                    height: 'auto',
                    padding: isClosed ? '10px 0' : '2px 0',
                    borderRadius: 2,
                    display: "flex",
                    alignItems: 'center',
                    width: "100%",
                    maxWidth: '100%',
                    backgroundColor: "transparent",
                    border: mov?.fromDocMov ? "1px solid rgb(150, 1, 1)" : "-moz-initial"
                  }}
                />
              </Grid>
            ))}
            <div ref={sentinelRef} style={{ height: 1 }} />
          </Grid>
        </Grid>
      </Grid>
    </>
  );
};
